import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent } from '@angular/common/http';
import { Observable } from 'rxjs';
import * as moment from 'moment';

/**
 * Angular/Moment serialize Date and Moment values to UTC ISO strings by default
 * (Date.prototype.toJSON / Moment.prototype.toJSON), which JSON.stringify uses when
 * HttpClient sends the request body. For a country ahead of UTC (Romania, UTC+2/+3),
 * local midnight shifts to the previous day once converted to UTC, so a date picked
 * as 01.08.2026 is sent to the backend as "2026-07-31T21:00:00.000Z".
 *
 * This interceptor rewrites every Date/Moment value in outgoing request bodies to a
 * local-time ISO-like string (no UTC conversion, no trailing "Z"), so the backend
 * receives exactly the wall-clock value the user picked.
 */
@Injectable()
export class LocalDateInterceptor implements HttpInterceptor {
    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const body = request.body;
        const shouldSkip =
            body === null ||
            typeof body !== 'object' ||
            (typeof FormData !== 'undefined' && body instanceof FormData) ||
            (typeof Blob !== 'undefined' && body instanceof Blob) ||
            (typeof ArrayBuffer !== 'undefined' && body instanceof ArrayBuffer);

        if (shouldSkip) {
            return next.handle(request);
        }

        return next.handle(request.clone({ body: this.normalizeDates(body) }));
    }

    private normalizeDates(value: any): any {
        if (value instanceof Date) {
            return this.formatLocalDateTime(value);
        }

        if (moment.isMoment(value)) {
            return value.clone().local().format('YYYY-MM-DDTHH:mm:ss');
        }

        if (Array.isArray(value)) {
            return value.map((item) => this.normalizeDates(item));
        }

        if (
            this.isRecursable(value)
        ) {
            const result: any = {};
            for (const key of Object.keys(value)) {
                result[key] = this.normalizeDates(value[key]);
            }
            return result;
        }

        return value;
    }

    private isRecursable(v: unknown): boolean {
        if (v === null || typeof v !== 'object') return false;
        if (ArrayBuffer.isView(v) || v instanceof ArrayBuffer) return false;
        if (v instanceof Map || v instanceof Set || v instanceof WeakMap || v instanceof WeakSet) return false;
        if (v instanceof RegExp || v instanceof Error || v instanceof Promise) return false;
        if (typeof URLSearchParams !== 'undefined' && v instanceof URLSearchParams) return false;
        if (typeof FormData !== 'undefined' && v instanceof FormData) return false;
        if (typeof Blob !== 'undefined' && v instanceof Blob) return false; // covers File
        return true;
    }

    private formatLocalDateTime(date: Date): string {
        const pad = (n: number) => String(n).padStart(2, '0');
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
            `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
    }
}
